import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { KeyRound, Save } from "lucide-react";
import { toast } from "sonner";

export default function DietitianProfile() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords don't match"); return; }
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-xl" data-testid="dietitian-profile-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Profile</p>
        <h1 className="text-3xl font-bold mt-1">{user?.name}</h1>
        <p className="text-muted-foreground mt-1">Your account details.</p>
      </header>

      <div className="nt-card p-6 space-y-4">
        <div className="space-y-2">
          <Label>Name</Label>
          <Input value={user?.name || ""} disabled readOnly data-testid="profile-name" />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input value={user?.email || ""} disabled readOnly className="text-muted-foreground" data-testid="profile-email" />
          <p className="text-xs text-muted-foreground">Email is managed by the super admin and cannot be changed here.</p>
        </div>
      </div>

      <div className="nt-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound size={18} className="text-primary" />
          <h2 className="font-semibold text-lg">Change password</h2>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div className="space-y-2">
            <Label>Current password</Label>
            <Input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="current-password-input" />
          </div>
          <div className="space-y-2">
            <Label>New password</Label>
            <Input type="password" required minLength={6} value={next} onChange={(e) => setNext(e.target.value)} data-testid="new-password-input" />
          </div>
          <div className="space-y-2">
            <Label>Confirm new password</Label>
            <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-password-input" />
          </div>
          <Button type="submit" disabled={saving} className="gap-2" data-testid="save-password-button">
            <Save size={16} /> {saving ? "Updating…" : "Update password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
